import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { Box, Button, Typography, useTheme, IconButton, Alert, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import { centeredRowStack } from '@ui/style/style';
import ImportErrorsDialog from './ImportErrorsDialog';
import { toast } from 'react-toastify';
import { useAppDispatch } from '@application/store/useStore';
import { downloadExcelTemplateAsync, uploadExcelAsync } from '@application/features/mvps/mvpsThunk';
const ImportExcelDialog = ({ open, onClose, isSmallScreen, eventId, onSuccess }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [selectedFile, setSelectedFile] = useState(null);
    const [showErrorsDialog, setShowErrorsDialog] = useState(false);
    const [uploadErrors, setUploadErrors] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file extension
            const fileExtension = file.name.split('.').pop()?.toLowerCase();
            if (fileExtension !== 'xlsx') {
                toast.error(t('events.eventDetails.rsvpTracking.invalidFileFormat') || 'Invalid file format. Only Excel files (.xlsx) are allowed.');
                // Reset the input
                event.target.value = '';
                return;
            }
            setSelectedFile(file);
            setUploadErrors([]);
        }
    };
    const handleDownloadTemplate = async () => {
        try {
            const result = await dispatch(downloadExcelTemplateAsync()).unwrap();
            // Create a blob from the response data
            const contentType = result.headers['content-type'] ||
                result.headers['Content-Type'] ||
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            const blob = new Blob([result.data], { type: contentType });
            // Create a download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            // Get filename from Content-Disposition header or use default
            const contentDisposition = result.headers['content-disposition'] ||
                result.headers['Content-Disposition'] ||
                '';
            let filename = 'RSVPTemplate.xlsx';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                    // Handle URL-encoded filenames
                    try {
                        filename = decodeURIComponent(filename);
                    }
                    catch (e) {
                        // If decoding fails, use the original filename
                    }
                }
            }
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            // Show success message
            toast.success(t('events.eventDetails.rsvpTracking.templateDownloadedSuccessfully') ||
                'RSVP template downloaded successfully!');
        }
        catch (error) {
            console.error('Error downloading template:', error);
            toast.error(error?.message ||
                t('events.eventDetails.rsvpTracking.failedToDownloadTemplate') ||
                'Failed to download template. Please try again.');
        }
    };
    const handleAdd = async () => {
        if (!selectedFile || !eventId) {
            return;
        }
        setIsUploading(true);
        try {
            const result = await dispatch(uploadExcelAsync({
                file: selectedFile,
                eventId: eventId
            })).unwrap();
            // Check if upload was successful
            if (result.isSuccess && result.data) {
                const successCount = result.data.totalRowsProcessed || 0;
                const errorCount = result.data.errors?.length || 0;
                const hasProcessedData = successCount > 0;
                const hasErrors = errorCount > 0;
                if (hasProcessedData) {
                    // Refresh the MVP list immediately so user can see added rows
                    if (onSuccess) {
                        onSuccess();
                    }
                    if (hasErrors && result.data.errors) {
                        // Partial success: some rows succeeded, some failed
                        setUploadErrors(result.data.errors);
                        setUploadResult({
                            successCount,
                            errorCount
                        });
                        // Show success toast with count
                        toast.success(t('events.eventDetails.rsvpTracking.partialImportSuccess', { count: successCount }) ||
                            `${successCount} RSVP(s) added successfully. ${errorCount} row(s) had errors.`);
                        // Don't close dialog - show summary instead
                        // Don't show error dialog immediately - let user see summary first
                    }
                    else {
                        // All rows succeeded - show success message and close
                        toast.success(result.message ||
                            t('events.eventDetails.rsvpTracking.importAllSuccess', { count: successCount }) ||
                            `${successCount} RSVP(s) added successfully!`);
                        // Close the dialog
                        setSelectedFile(null);
                        setUploadResult(null);
                        // Reset file input
                        const fileInput = document.getElementById('excel-file-input');
                        if (fileInput) {
                            fileInput.value = '';
                        }
                        onClose();
                    }
                }
                else {
                    // No data was processed - show errors only
                    const errors = result.data?.errors || result.errors || [result.message || 'No data was processed.'];
                    setUploadErrors(errors);
                    setUploadResult(null);
                    setShowErrorsDialog(true);
                }
            }
            else {
                // Upload failed - check for errors in data first, then fallback to message
                const errors = result.data?.errors || result.errors || [result.message || 'Failed to upload Excel file.'];
                setUploadErrors(errors);
                setUploadResult(null);
                setShowErrorsDialog(true);
            }
        }
        catch (error) {
            console.error('Error uploading Excel file:', error);
            const errorMessage = error?.message ||
                error?.error ||
                t('events.eventDetails.rsvpTracking.importError') ||
                'Failed to upload Excel file. Please try again.';
            toast.error(errorMessage);
            setUploadErrors([errorMessage]);
            setShowErrorsDialog(true);
        }
        finally {
            setIsUploading(false);
        }
    };
    const handleCancel = () => {
        setSelectedFile(null);
        setUploadErrors([]);
        setUploadResult(null);
        setShowErrorsDialog(false);
        // Reset file input
        const fileInput = document.getElementById('excel-file-input');
        if (fileInput) {
            fileInput.value = '';
        }
        onClose();
    };
    const handleViewErrors = () => {
        setShowErrorsDialog(true);
    };
    const handleCloseAfterErrors = () => {
        setShowErrorsDialog(false);
        setUploadErrors([]);
        setUploadResult(null);
        setSelectedFile(null);
        // Reset file input
        const fileInput = document.getElementById('excel-file-input');
        if (fileInput) {
            fileInput.value = '';
        }
        onClose();
    };
    const dialogContent = (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2, p: isSmallScreen ? 1 : 2 }, children: [uploadResult && uploadResult.successCount > 0 && uploadResult.errorCount > 0 && (_jsx(Alert, { color: 'warning', variant: 'outlined', sx: {
                    backgroundColor: alpha(theme.palette.warning.main, 0.08),
                    borderColor: alpha(theme.palette.warning.main, 0.16),
                    color: theme.palette.warning.dark,
                    alignItems: 'flex-start',
                    '& .MuiAlert-message': {
                        padding: 0,
                        width: '100%',
                        flex: 1
                    },
                    '& .MuiAlert-action': {
                        paddingTop: 0,
                        alignItems: 'flex-start',
                        marginRight: 0
                    },
                }, icon: _jsx(Icon, { icon: 'solar:info-circle-bold' }), action: _jsx(Button, { size: 'small', variant: 'outlined', color: 'warning', onClick: handleViewErrors, endIcon: _jsx(Icon, { icon: 'eva:arrow-forward-outline', fontSize: 16 }), sx: {
                        flexShrink: 0,
                        whiteSpace: 'nowrap'
                    }, children: t('events.eventDetails.rsvpTracking.errors') || 'Errors' }), children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 0.5 }, children: [_jsx(Typography, { variant: 'body2', fontWeight: 500, children: t('events.eventDetails.rsvpTracking.partialImportSummary', {
                                success: uploadResult.successCount,
                                errors: uploadResult.errorCount
                            }) || `${uploadResult.successCount} RSVP(s) added successfully. ${uploadResult.errorCount} row(s) had errors.` }), _jsx(Typography, { variant: 'caption', sx: { opacity: 0.8 }, children: t('events.eventDetails.rsvpTracking.viewErrorsHint') || 'Click "Errors" to see details about the failed rows.' })] }) })), _jsx(Alert, { color: 'info', variant: 'outlined', sx: {
                    backgroundColor: alpha(theme.palette.info.main, 0.08),
                    borderColor: alpha(theme.palette.info.main, 0.16),
                    color: theme.palette.info.dark,
                    alignItems: "center",
                    '& .MuiAlert-message': {
                        padding: 0,
                    },
                }, icon: _jsx(Icon, { icon: 'solar:info-circle-bold' }), action: _jsx(Button, { size: 'small', variant: 'contained', sx: { backgroundColor: theme.palette.info.main, color: theme.palette.info.contrastText }, endIcon: _jsx(Icon, { icon: 'eva:cloud-download-outline', fontSize: 18, color: theme.palette.info.contrastText }), onClick: handleDownloadTemplate, children: t('events.eventDetails.rsvpTracking.template') || 'Template' }), children: t('events.eventDetails.rsvpTracking.excelFormatInfo') ||
                    'Please follow the format for the .xlsx file that you are uploading.' }), _jsxs(Box, { sx: {
                    width: '100%',
                    border: `1px solid ${theme.palette.grey[400]}`,
                    borderRadius: '16px',
                    background: theme.palette.common.grey8,
                    padding: 2,
                    minHeight: '48px'
                }, children: [_jsx(Typography, { variant: 'subtitle2', children: t('events.eventDetails.rsvpTracking.uploadExcelFile') || 'Upload Excel File' }), _jsx(Typography, { variant: 'body2', my: 1, children: t('events.eventDetails.rsvpTracking.uploadExcelDescription') || 'Upload Excel File' }), _jsxs(Box, { sx: {
                            border: `1px solid ${theme.palette.success.dark}`,
                            borderRadius: '12px',
                            width: '100%',
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.success.dark,
                            cursor: 'pointer',
                            position: 'relative'
                        }, onClick: () => {
                            const fileInput = document.getElementById('excel-file-input');
                            if (fileInput) {
                                fileInput.click();
                            }
                        }, children: [_jsx("input", { id: 'excel-file-input', type: 'file', accept: '.xlsx', style: { display: 'none' }, onChange: handleFileChange }), _jsx(Box, { sx: {
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    flex: 1,
                                    px: 1,
                                    color: theme.palette.success.dark,
                                    fontSize: 13,
                                    fontWeight: 500
                                }, children: selectedFile ? (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, width: '100%' }, children: [_jsx(Typography, { variant: 'subtitle2', noWrap: true, sx: { minWidth: 0, maxWidth: '100%', flex: 1 }, children: selectedFile.name }), _jsx(IconButton, { size: 'small', onClick: (e) => {
                                                e.stopPropagation();
                                                setSelectedFile(null);
                                            }, sx: {
                                                padding: 0.5,
                                                '&:hover': {
                                                    backgroundColor: alpha(theme.palette.error.main, 0.1)
                                                }
                                            }, children: _jsx(Icon, { icon: 'solar:close-circle-bold', width: 16, color: theme.palette.error.main }) })] })) : (_jsx(Typography, { variant: 'subtitle2', noWrap: true, sx: { minWidth: 0, maxWidth: '100%' }, children: t('events.eventDetails.rsvpTracking.import') || 'Import' })) })] })] })] }));
    const actionButtons = (_jsxs(Box, { sx: { ...centeredRowStack }, width: '100%', gap: 1, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: handleCancel, size: 'large', children: t('actions.cancel') || 'Cancel' }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', onClick: handleAdd, size: 'large', disabled: !selectedFile || isUploading, sx: {
                    backgroundColor: theme.palette.grey[800],
                    '&:hover': {
                        backgroundColor: theme.palette.grey[700]
                    }
                }, children: t('actions.addNow') || 'Add now' })] }));
    return (_jsxs(_Fragment, { children: [!isSmallScreen && (_jsx(CustomDialog, { open: open, onClose: handleCancel, titleWithIcon: t('events.eventDetails.rsvpTracking.addNewRSVP') || 'Add New RSVP', titleIcon: '/assets/images/add-person.png', actions: actionButtons, children: dialogContent })), isSmallScreen && (_jsx(BDrawer, { anchor: 'bottom', title: t('events.eventDetails.rsvpTracking.addNewRSVP') || 'Add New RSVP', open: open, onClose: handleCancel, showCloseButton: true, dialogActions: actionButtons, children: _jsx(Box, { sx: { flex: 1, overflow: 'auto' }, children: dialogContent }) })), _jsx(ImportErrorsDialog, { open: showErrorsDialog, onClose: uploadResult ? handleCloseAfterErrors : () => {
                    setShowErrorsDialog(false);
                    setUploadErrors([]);
                    setSelectedFile(null);
                    onClose();
                }, onBack: () => {
                    setShowErrorsDialog(false);
                }, isSmallScreen: isSmallScreen, errors: uploadErrors })] }));
};
export default ImportExcelDialog;
